import React, { useEffect, useState, useMemo } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import moment from "moment";
import "moment/locale/pt-br";
import { useNavigate } from "react-router-dom";

import {
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PaymentsIcon from "@mui/icons-material/Payments";
import BadgeIcon from "@mui/icons-material/Badge";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

moment.locale("pt-br");

// ─── Paleta ────────────────────────────────────────────────────────────────────
const PALETTE = {
  bg: "#0f1117",
  surface: "#181c27",
  surfaceHigh: "#1e2333",
  border: "#2a3048",
  accent: "#e05c5c",
  accentSoft: "rgba(224,92,92,0.12)",
  accentEntrada: "#f59e0b",
  accentParcela: "#3b82f6",
  accentCancel: "#8b5cf6",
  text: "#e8eaf0",
  textMuted: "#7a82a0",
  success: "#22c55e",
  chartColors: [
    "#e05c5c",
    "#f59e0b",
    "#3b82f6",
    "#8b5cf6",
    "#06b6d4",
    "#ec4899",
  ],
};

// ─── Styled helpers ────────────────────────────────────────────────────────────
const Root = styled(Box)({
  background: PALETTE.bg,
  minHeight: "100%",
  padding: "24px",
  fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
  color: PALETTE.text,
});

const Surface = styled(Paper)({
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.border}`,
  borderRadius: 12,
  boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
});

const StatCard = styled(Card)(({ accentcolor }) => ({
  background: PALETTE.surfaceHigh,
  border: `1px solid ${accentcolor || PALETTE.border}`,
  borderRadius: 10,
  boxShadow: `0 0 20px ${accentcolor ? accentcolor + "22" : "transparent"}`,
  height: "100%",
}));

const StyledTab = styled(Tab)(({ accentcolor }) => ({
  color: PALETTE.textMuted,
  fontWeight: 600,
  fontSize: "0.82rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  minHeight: 48,
  "&.Mui-selected": {
    color: accentcolor || PALETTE.accent,
  },
}));

const StyledTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    background: PALETTE.accent,
  },
  borderBottom: `1px solid ${PALETTE.border}`,
  marginBottom: 20,
});

const MsgChip = styled(Chip)(({ msgtype }) => ({
  fontSize: "0.7rem",
  height: 22,
  fontWeight: 700,
  letterSpacing: "0.06em",
  backgroundColor:
    msgtype === "error"
      ? "rgba(224,92,92,0.18)"
      : msgtype === "warning"
        ? "rgba(245,158,11,0.18)"
        : "rgba(59,130,246,0.18)",
  color:
    msgtype === "error"
      ? PALETTE.accent
      : msgtype === "warning"
        ? PALETTE.accentEntrada
        : PALETTE.accentParcela,
  border: `1px solid ${
    msgtype === "error"
      ? PALETTE.accent
      : msgtype === "warning"
        ? PALETTE.accentEntrada
        : PALETTE.accentParcela
  }44`,
}));

const OrigemBadge = styled(Box)(({ origem }) => {
  const map = {
    entrada: { bg: "rgba(245,158,11,0.12)", color: PALETTE.accentEntrada },
    parcela: { bg: "rgba(59,130,246,0.12)", color: PALETTE.accentParcela },
    cancelamento: { bg: "rgba(139,92,246,0.12)", color: PALETTE.accentCancel },
  };
  const s = map[origem] || map.entrada;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    background: s.bg,
    color: s.color,
    border: `1px solid ${s.color}44`,
  };
});

const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    background: PALETTE.surfaceHigh,
    borderRadius: 8,
    color: PALETTE.text,
    fontSize: "0.88rem",
    "& fieldset": { borderColor: PALETTE.border },
    "&:hover fieldset": { borderColor: PALETTE.textMuted },
    "&.Mui-focused fieldset": { borderColor: PALETTE.accent },
  },
  "& .MuiInputAdornment-root svg": { color: PALETTE.textMuted },
});

const StyledAccordion = styled(Accordion)({
  background: PALETTE.surfaceHigh,
  border: `1px solid ${PALETTE.border}`,
  borderRadius: "8px !important",
  marginBottom: 8,
  "&:before": { display: "none" },
  "&.Mui-expanded": {
    borderColor: PALETTE.accent + "66",
    boxShadow: `0 0 16px rgba(224,92,92,0.08)`,
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  "& .MuiAccordionSummary-expandIconWrapper svg": { color: PALETTE.textMuted },
  padding: "4px 16px",
});

const StyledTableCell = styled(TableCell)({
  color: PALETTE.textMuted,
  borderBottom: `1px solid ${PALETTE.border}`,
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  padding: "10px 14px",
});

const StyledTableBodyCell = styled(TableCell)({
  color: PALETTE.text,
  borderBottom: `1px solid ${PALETTE.border}22`,
  fontSize: "0.82rem",
  padding: "10px 14px",
});

const SectionLabel = styled(Typography)({
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: PALETTE.textMuted,
  marginBottom: 12,
});

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.5,
      borderBottom: `1px solid ${PALETTE.border}22`,
    }}
  >
    <Typography sx={{ fontSize: "0.78rem", color: PALETTE.textMuted }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.78rem", color: PALETTE.text, fontWeight: 600 }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

// ─── Formatadores ─────────────────────────────────────────────────────────────
const fmtCurrency = (val) =>
  val != null
    ? val.toLocaleString("pt-CV", { style: "currency", currency: "CVE" })
    : "—";

const fmtDate = (d) => (d ? moment(d).format("DD/MM/YYYY") : "—");
const fmtDateTime = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm") : "—");

// ─── Labels de origem ─────────────────────────────────────────────────────────
const ORIGEM_LABEL = {
  entrada: "Entrada",
  parcela: "Parcela",
  cancelamento: "Cancelamento",
};

const ORIGEM_ICON = {
  entrada: <ReceiptLongIcon sx={{ fontSize: 13, mr: 0.4 }} />,
  parcela: <AccountBalanceWalletIcon sx={{ fontSize: 13, mr: 0.4 }} />,
  cancelamento: <CancelIcon sx={{ fontSize: 13, mr: 0.4 }} />,
};

// ─── Tooltip customizado para gráficos ────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: PALETTE.surfaceHigh,
        border: `1px solid ${PALETTE.border}`,
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Typography
        sx={{ fontSize: "0.78rem", color: PALETTE.textMuted, mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.9rem", fontWeight: 700, color: PALETTE.text }}
      >
        {payload[0].value} docs
      </Typography>
    </Box>
  );
};

// ─── Cartão de estatística ────────────────────────────────────────────────────
const StatCardItem = ({ icon, label, value, accent }) => (
  <StatCard accentcolor={accent}>
    <CardContent sx={{ p: "16px !important" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ color: accent, display: "flex" }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: PALETTE.textMuted,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{ fontSize: "2rem", fontWeight: 800, color: accent, lineHeight: 1 }}
      >
        {value}
      </Typography>
    </CardContent>
  </StatCard>
);

// ─── Card de documento ────────────────────────────────────────────────────────
const DocCard = ({ doc, expanded, onToggle }) => {
  const accentMap = {
    entrada: PALETTE.accentEntrada,
    parcela: PALETTE.accentParcela,
    cancelamento: PALETTE.accentCancel,
  };
  const accent = accentMap[doc.origem] || PALETTE.accent;
  const navigate = useNavigate();
  const clienteNome =
    typeof doc.cliente === "object"
      ? doc.cliente?.nome || doc.cliente?.nomeFiscal || "Não identificado"
      : doc.cliente === "não identificado"
        ? "Não identificado"
        : doc.cliente || "Não identificado";

  return (
    <StyledAccordion expanded={expanded} onChange={onToggle}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container alignItems="center" spacing={1}>
          {/* Col 1 — documento */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}
            >
              <OrigemBadge origem={doc.origem}>
                {ORIGEM_ICON[doc.origem]}
                {ORIGEM_LABEL[doc.origem]}
              </OrigemBadge>
            </Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.9rem", color: PALETTE.text }}
            >
              {doc.tipoDocumento || "—"}{" "}
              <span style={{ color: accent }}>{doc.nrDocumento || ""}</span>
            </Typography>
            {doc.serie && (
              <Typography
                sx={{ fontSize: "0.72rem", color: PALETTE.textMuted }}
              >
                Série: {doc.serie}
              </Typography>
            )}
          </Grid>

          {/* Col 2 — cliente */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PersonOutlineIcon
                sx={{ fontSize: 15, color: PALETTE.textMuted }}
              />
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: PALETTE.text,
                  fontWeight: 600,
                }}
              >
                {clienteNome}
              </Typography>
            </Box>
            {doc.usuarioEmissor && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.3,
                }}
              >
                <BadgeIcon sx={{ fontSize: 13, color: PALETTE.textMuted }} />
                <Typography
                  sx={{ fontSize: "0.72rem", color: PALETTE.textMuted }}
                >
                  {doc.usuarioEmissor?.nome || "—"}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Col 3 — data / valor */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon
                sx={{ fontSize: 13, color: PALETTE.textMuted }}
              />
              <Typography
                sx={{ fontSize: "0.78rem", color: PALETTE.textMuted }}
              >
                {fmtDate(doc.created_at)}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}
            >
              <PaymentsIcon sx={{ fontSize: 13, color: accent }} />
              <Typography
                sx={{ fontSize: "0.82rem", fontWeight: 700, color: accent }}
              >
                {fmtCurrency(doc.valor)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </StyledAccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
        <Divider sx={{ borderColor: PALETTE.border, mb: 2 }} />

        {/* Mensagens de erro */}
        <SectionLabel>Mensagens de erro</SectionLabel>
        {doc.messages?.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              background: PALETTE.bg,
              border: `1px solid ${PALETTE.border}`,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Código</StyledTableCell>
                  <StyledTableCell>Descrição</StyledTableCell>
                  <StyledTableCell>Tipo</StyledTableCell>
                  <StyledTableCell>Data/Hora</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doc.messages.map((msg, i) => (
                  <TableRow key={i} sx={{ "&:last-child td": { border: 0 } }}>
                    <StyledTableBodyCell>
                      <MsgChip
                        label={msg.code || "—"}
                        size="small"
                        msgtype={msg.type}
                      />
                    </StyledTableBodyCell>
                    <StyledTableBodyCell>
                      {msg.description || "—"}
                    </StyledTableBodyCell>
                    <StyledTableBodyCell>{msg.type || "—"}</StyledTableBodyCell>
                    <StyledTableBodyCell>
                      {fmtDateTime(msg.dateTime)}
                    </StyledTableBodyCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              background: "rgba(59,130,246,0.08)",
              color: PALETTE.textMuted,
              fontSize: "0.8rem",
            }}
          >
            Nenhuma mensagem de erro registada.
          </Alert>
        )}

        {/* Detalhes do documento */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SectionLabel>Informação do documento</SectionLabel>
            <Box
              sx={{
                background: PALETTE.bg,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <InfoRow label="Tipo" value={doc.tipoDocumento} />
              <InfoRow label="Nº Documento" value={doc.nrDocumento} />
              <InfoRow label="Série" value={doc.serie} />
              <InfoRow label="Valor" value={fmtCurrency(doc.valor)} />
              <InfoRow
                label="Data de emissão"
                value={fmtDate(doc.created_at)}
              />
              <InfoRow label="Método pagamento" value={doc.metodoPagamento} />
              <InfoRow
                label="Data pagamento"
                value={fmtDate(doc.dataPagamento)}
              />
              <InfoRow
                label="Anulado"
                value={doc.anulacao?.anulado ? "Sim" : "Não"}
              />
              {doc.emitidoEmContingencia && (
                <InfoRow
                  label="Contingência"
                  value={fmtDate(doc.dataEmissaoContingencia)}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SectionLabel>Cliente / Entidade</SectionLabel>
            <Box
              sx={{
                background: PALETTE.bg,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <InfoRow
                label="Nome"
                value={
                  typeof doc.cliente === "object" && doc.cliente?._id ? (
                    <span
                      onClick={() =>
                        navigate(`/clientes/${doc.cliente._id}-debitos`)
                      }
                      style={{
                        color: PALETTE.accentParcela,
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.textDecoration = "underline")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.textDecoration = "none")
                      }
                    >
                      {clienteNome}
                    </span>
                  ) : (
                    clienteNome
                  )
                }
              />
              {typeof doc.cliente === "object" && (
                <>
                  <InfoRow label="NIF" value={doc.cliente?.nif} />
                  <InfoRow
                    label="Nome fiscal"
                    value={doc.cliente?.nomeFiscal}
                  />
                </>
              )}
              <InfoRow label="Emissor" value={doc.usuarioEmissor?.nome} />
              <InfoRow label="Origem" value={ORIGEM_LABEL[doc.origem]} />
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </StyledAccordion>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
function RelatorioDocumentosEletronicosComErro({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  unidadeSelecionada = "",
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();

  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedDoc, setExpandedDoc] = useState(null);

  useEffect(() => {
    if (acao === "gerar") gerarRelatorio();
  }, [acao, atualizacoes]);

  const gerarRelatorio = async () => {
    if (!dadosClinica?._id) return;
    setError(null);
    startLoading();
    try {
      const formData = new FormData();
      formData.append("startDate", dataFiltro.dataInicial.format("YYYY-MM-DD"));
      formData.append("endDate", dataFiltro.dataFinal.format("YYYY-MM-DD"));
      // Vazio = consolidado da empresa.
      if (unidadeSelecionada)
        formData.append("estabelecimento", unidadeSelecionada);

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/getRelatorioDocumentosErros/${dadosClinica._id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );
      // Separar a lista plana em grupos por origem
      const lista = responseData || [];
      setDados({
        entrada: lista.filter((d) => d.origem === "entrada"),
        parcela: lista.filter((d) => d.origem === "parcela"),
        cancelamento: lista.filter((d) => d.origem === "cancelamento"),
        total: lista.length,
      });
      setExpandedDoc(null);
      setSearchTerm("");
    } catch (err) {
      setError(err.message || "Erro ao gerar o relatório.");
    } finally {
      stopLoading();
    }
  };

  const tabDocs = useMemo(() => {
    if (!dados) return [];
    const map = ["entrada", "parcela", "cancelamento"];
    return dados[map[tabValue]] || [];
  }, [dados, tabValue]);

  const docsFiltered = useMemo(() => {
    if (!searchTerm) return tabDocs;
    const q = searchTerm.toLowerCase();
    return tabDocs.filter(
      (d) =>
        d.nrDocumento?.toLowerCase().includes(q) ||
        d.tipoDocumento?.toLowerCase().includes(q) ||
        d.serie?.toLowerCase().includes(q) ||
        (typeof d.cliente === "object"
          ? d.cliente?.nome?.toLowerCase().includes(q) ||
            d.cliente?.nomeFiscal?.toLowerCase().includes(q)
          : false) ||
        d.messages?.some(
          (m) =>
            m.description?.toLowerCase().includes(q) ||
            m.code?.toLowerCase().includes(q),
        ),
    );
  }, [tabDocs, searchTerm]);

  // Dados gráficos — códigos de erro (todas as origens)
  const dadosGraficoCodigos = useMemo(() => {
    if (!dados) return [];
    const all = [
      ...(dados.entrada || []),
      ...(dados.parcela || []),
      ...(dados.cancelamento || []),
    ];
    const acc = {};
    for (const d of all) {
      for (const m of d.messages || []) {
        const k = m.code || "Desconhecido";
        acc[k] = (acc[k] || 0) + 1;
      }
    }
    return Object.entries(acc)
      .map(([codigo, qty]) => ({ codigo, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 8);
  }, [dados]);

  // Dados gráficos — por origem
  const dadosGraficoOrigens = useMemo(() => {
    if (!dados) return [];
    return [
      {
        name: "Entradas",
        qty: dados.entrada?.length || 0,
        color: PALETTE.accentEntrada,
      },
      {
        name: "Parcelas",
        qty: dados.parcela?.length || 0,
        color: PALETTE.accentParcela,
      },
      {
        name: "Cancelamentos",
        qty: dados.cancelamento?.length || 0,
        color: PALETTE.accentCancel,
      },
    ].filter((d) => d.qty > 0);
  }, [dados]);

  const TAB_CONFIG = [
    {
      label: "Entradas",
      icon: <ReceiptLongIcon sx={{ fontSize: 16 }} />,
      accent: PALETTE.accentEntrada,
      key: "entrada",
    },
    {
      label: "Parcelas",
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 16 }} />,
      accent: PALETTE.accentParcela,
      key: "parcela",
    },
    {
      label: "Cancelamentos",
      icon: <CancelIcon sx={{ fontSize: 16 }} />,
      accent: PALETTE.accentCancel,
      key: "cancelamento",
    },
  ];

  return (
    <Root>
      <Surface sx={{ p: 3, mb: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ErrorOutlineIcon sx={{ color: PALETTE.accent, fontSize: 22 }} />
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: 800,
                color: PALETTE.text,
                letterSpacing: "-0.02em",
              }}
            >
              Documentos Eletrónicos com Erro
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.8rem", color: PALETTE.textMuted }}>
            {dadosClinica?.nome} ·{" "}
            {dataFiltro?.dataInicial?.format("DD/MM/YYYY")} –{" "}
            {dataFiltro?.dataFinal?.format("DD/MM/YYYY")}
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              background: "rgba(224,92,92,0.1)",
              color: PALETTE.accent,
              border: `1px solid ${PALETTE.accent}44`,
            }}
          >
            {error}
          </Alert>
        )}

        {dados && dados.total === 0 && (
          <Alert
            severity="info"
            sx={{
              background: "rgba(59,130,246,0.08)",
              color: PALETTE.textMuted,
            }}
          >
            Nenhum documento eletrónico com erro encontrado para o período
            selecionado.
          </Alert>
        )}

        {dados && dados.total > 0 && (
          <>
            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3}>
                <StatCardItem
                  icon={<ErrorOutlineIcon />}
                  label="Total"
                  value={dados.total}
                  accent={PALETTE.accent}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCardItem
                  icon={<ReceiptLongIcon />}
                  label="Entradas"
                  value={dados.entrada.length}
                  accent={PALETTE.accentEntrada}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCardItem
                  icon={<AccountBalanceWalletIcon />}
                  label="Parcelas"
                  value={dados.parcela.length}
                  accent={PALETTE.accentParcela}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCardItem
                  icon={<CancelIcon />}
                  label="Cancelamentos"
                  value={dados.cancelamento.length}
                  accent={PALETTE.accentCancel}
                />
              </Grid>
            </Grid>

            {/* Gráficos */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={7}>
                <Surface sx={{ p: 2.5, height: "100%" }}>
                  <SectionLabel sx={{ mb: 2 }}>Erros por código</SectionLabel>
                  {dadosGraficoCodigos.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={dadosGraficoCodigos}
                        margin={{ top: 0, right: 10, left: -20, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={PALETTE.border}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="codigo"
                          tick={{ fill: PALETTE.textMuted, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: PALETTE.textMuted, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                        />
                        <RechartsTooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: "rgba(255,255,255,0.03)" }}
                        />
                        <Bar
                          dataKey="qty"
                          name="Ocorrências"
                          radius={[4, 4, 0, 0]}
                        >
                          {dadosGraficoCodigos.map((_, i) => (
                            <Cell
                              key={i}
                              fill={
                                PALETTE.chartColors[
                                  i % PALETTE.chartColors.length
                                ]
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography
                      sx={{ color: PALETTE.textMuted, fontSize: "0.8rem" }}
                    >
                      Sem dados de códigos de erro.
                    </Typography>
                  )}
                </Surface>
              </Grid>

              <Grid item xs={12} md={5}>
                <Surface sx={{ p: 2.5, height: "100%" }}>
                  <SectionLabel sx={{ mb: 2 }}>
                    Distribuição por origem
                  </SectionLabel>
                  {dadosGraficoOrigens.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={dadosGraficoOrigens}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          dataKey="qty"
                          nameKey="name"
                          paddingAngle={3}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={{ stroke: PALETTE.border }}
                        >
                          {dadosGraficoOrigens.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Legend
                          formatter={(value) => (
                            <span
                              style={{ color: PALETTE.textMuted, fontSize: 11 }}
                            >
                              {value}
                            </span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography
                      sx={{ color: PALETTE.textMuted, fontSize: "0.8rem" }}
                    >
                      Sem dados.
                    </Typography>
                  )}
                </Surface>
              </Grid>
            </Grid>

            {/* Tabs */}
            <StyledTabs
              value={tabValue}
              onChange={(_, v) => {
                setTabValue(v);
                setSearchTerm("");
                setExpandedDoc(null);
              }}
            >
              {TAB_CONFIG.map((t, i) => (
                <StyledTab
                  key={t.key}
                  accentcolor={t.accent}
                  icon={t.icon}
                  iconPosition="start"
                  label={`${t.label} (${dados[t.key]?.length || 0})`}
                />
              ))}
            </StyledTabs>

            {/* Pesquisa */}
            <Box sx={{ mb: 3 }}>
              <SearchField
                fullWidth
                size="small"
                placeholder="Pesquisar por número, tipo, cliente, série ou mensagem de erro…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Lista de documentos */}
            {docsFiltered.length > 0 ? (
              docsFiltered.map((doc) => (
                <DocCard
                  key={doc._id}
                  doc={doc}
                  expanded={expandedDoc === doc._id}
                  onToggle={() =>
                    setExpandedDoc(expandedDoc === doc._id ? null : doc._id)
                  }
                />
              ))
            ) : (
              <Alert
                severity="info"
                sx={{
                  background: "rgba(59,130,246,0.08)",
                  color: PALETTE.textMuted,
                }}
              >
                Nenhum documento encontrado
                {searchTerm ? " para a pesquisa efectuada" : ""}.
              </Alert>
            )}
          </>
        )}
      </Surface>
    </Root>
  );
}

export default RelatorioDocumentosEletronicosComErro;
